import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Headphones,
  Phone,
  Mail,
  Clock,
  Send,
  Loader2,
  MessageSquare,
  CheckCircle,
  HelpCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

interface ContactSupportProps {
  merchantName?: string;
  merchantEmail?: string;
}

const enquirySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email address").max(255),
  subject: z.string().min(1, "Please select a subject"),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(1000, "Message too long"),
});

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ContactSupport({ merchantName, merchantEmail }: ContactSupportProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"contact" | "form">("contact");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Check if user is logged in (has merchant details)
  const isLoggedIn = Boolean(merchantName && merchantEmail);
  
  const [formData, setFormData] = useState({
    name: merchantName || "",
    email: merchantEmail || "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async () => {
    try {
      const validated = enquirySchema.parse(formData);
      setErrors({});
      setSubmitting(true);

      // Send enquiry email via edge function
      const { error } = await supabase.functions.invoke("notify-merchant", {
        body: {
          type: "support_enquiry",
          merchantEmail: "support@shakatah.com",
          data: {
            name: validated.name,
            email: validated.email,
            subject: validated.subject,
            message: validated.message,
          },
        },
      });

      if (error) throw error;

      setSubmitted(true);
      toast.success("Your enquiry has been submitted. We'll get back to you soon!");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        console.error("Error submitting enquiry:", error);
        toast.error("Failed to submit enquiry. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: merchantName || "",
      email: merchantEmail || "",
      subject: "",
      message: "",
    });
    setSubmitted(false);
    setErrors({});
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Headphones className="h-4 w-4" />
          <span className="hidden sm:inline">Talk to Us</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="h-5 w-5 text-primary" />
            Contact Shakatah Support
          </DialogTitle>
          <DialogDescription>
            We're here to help! Choose how you'd like to reach us.
          </DialogDescription>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b pb-3">
          <Button
            variant={activeTab === "contact" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("contact")}
          >
            <Phone className="h-4 w-4 mr-2" />
            Contact Info
          </Button>
          <Button
            variant={activeTab === "form" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("form")}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Send Enquiry
          </Button>
        </div>

        {activeTab === "contact" && (
          <div className="space-y-4 pt-2">
            {/* Phone */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">Call Center</h4>
                    <a
                      href="mailto:support@shakatah.com"
                      className="text-lg font-mono text-primary hover:underline"
                    >
                      support@shakatah.com
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">
                      24/7 Global Support
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Working Hours */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">Working Hours</h4>
                    <div className="space-y-1 mt-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Monday - Friday</span>
                        <span className="font-medium">24/7 Email Support</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Response Time</span>
                        <span className="font-medium">Within 24 hours</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">Email Support</h4>
                    <a
                      href="mailto:support@shakatah.com"
                      className="text-primary hover:underline"
                    >
                      support@shakatah.com
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
              <HelpCircle className="h-3 w-3" />
              <span>Need quick answers?</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-primary hover:underline font-medium"
              >
                Check our FAQ
              </button>
            </div>
          </div>
        )}

        {activeTab === "form" && (
          <div className="space-y-4 pt-2">
            {submitted ? (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Enquiry Submitted!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Thank you for reaching out. Our support team will get back to you
                  within 24 hours.
                </p>
                <Button variant="outline" onClick={resetForm}>
                  Submit Another Enquiry
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-4">
                  {/* Only show name/email fields if not logged in */}
                  {!isLoggedIn && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Your Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          className={errors.name ? "border-red-500" : ""}
                        />
                        {errors.name && (
                          <p className="text-xs text-red-500">{errors.name}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@store.com"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          className={errors.email ? "border-red-500" : ""}
                        />
                        {errors.email && (
                          <p className="text-xs text-red-500">{errors.email}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Select
                      value={formData.subject}
                      onValueChange={(v) => handleInputChange("subject", v)}
                    >
                      <SelectTrigger className={errors.subject ? "border-red-500" : ""}>
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="billing">Billing & Subscription</SelectItem>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="integration">Widget Integration</SelectItem>
                        <SelectItem value="payments">Payment Problems</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.subject && (
                      <p className="text-xs text-red-500">{errors.subject}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Describe your issue or enquiry in detail..."
                      rows={4}
                      value={formData.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      className={errors.message ? "border-red-500" : ""}
                    />
                    {errors.message && (
                      <p className="text-xs text-red-500">{errors.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground text-right">
                      {formData.message.length}/1000
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Enquiry
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
